import {
  action,
  Action,
  ActionCreator,
  computed,
  Computed,
  Thunk,
  thunk,
  ThunkOn,
  thunkOn,
} from "easy-peasy";
import { StoreModel } from "./model";
import { Injections } from "./store-injections";
import { useStoreActions, useStoreState } from "../hooks/ep";
import { UserRole, VALID_NEW_USER_ROLES } from "./model-me";
import { VALID_TIMEZONES } from "../common/valid-timezones";
import _ from "lodash";

export interface DrawerFormModel {
  NAME: string;
  //
  resetData: Thunk<DrawerFormModel, void, Injections, StoreModel>;
  onLogin: ThunkOn<DrawerFormModel, Injections, StoreModel>;
  onResetMeData: ThunkOn<DrawerFormModel, Injections, StoreModel>;
  //
  initialData: null | InitialData;
  title: null | string;
  ContentComponent: null | any;
  handleSubmit: null | any;
  formStoreModelFactory: null | any;
  isOpen: Computed<DrawerFormModel, boolean, StoreModel>;
  open: Action<
    DrawerFormModel,
    {
      initialData: InitialData;
      title: string;
      ContentComponent: any;
      handleSubmit: any;
    }
  >;
  close: Action<DrawerFormModel>;
  //
  onOpen: ThunkOn<DrawerFormModel, Injections, StoreModel>;
  fieldName2Value: FieldName2Value;
  setField: Action<DrawerFormModel, SetFieldPayload>;
  dataIsSubmittable: Computed<DrawerFormModel, boolean>;
  errorMessage: Computed<DrawerFormModel, null | string>;
}

export const drawerFormModel: DrawerFormModel = {
  NAME: "drawerForm",
  //
  resetData: thunk((actions) => {
    actions.close();
  }),
  onLogin: thunkOn(
    (__, storeActions) => storeActions.me.receiveInitialData,
    (actions, target, { getStoreState }) => {
      actions.resetData();
    }
  ),
  onResetMeData: thunkOn(
    (__, storeActions) => storeActions.me.resetData,
    (actions) => {
      actions.resetData();
    }
  ),
  //
  initialData: null,
  title: null,
  ContentComponent: null,
  handleSubmit: null,
  formStoreModelFactory: null,
  isOpen: computed([(s) => s.initialData], (initialData) => !!initialData),
  open: action(
    (state, { initialData, title, ContentComponent, handleSubmit }) => {
      state.initialData = initialData;
      state.title = title;
      state.ContentComponent = ContentComponent;
      state.handleSubmit = handleSubmit;
    }
  ),
  close: action((state) => {
    state.fieldName2Value = {};
    state.initialData = null;
    state.title = null;
    state.ContentComponent = null;
    state.handleSubmit = null;
  }),
  //
  fieldName2Value: {},
  setField: action((state, { name, value }) => {
    state.fieldName2Value[name] = (value || "").trim();
  }),
  onOpen: thunkOn(
    (a, __) => a.open,
    (actions, { payload: { initialData } }) => {
      Object.entries(initialData?.fieldName2Value ?? {}).forEach(
        ([name, value]) => {
          actions.setField({ name, value });
        }
      );
    }
  ),
  dataIsSubmittable: computed(
    [(s) => s.errorMessage],
    (errorMessage) => !errorMessage
  ),
  errorMessage: computed([(s) => s.fieldName2Value], (fieldName2Value) => {
    const { email, first_name, last_name, role, timezone } = fieldName2Value;

    if (_.isNil(email)) {
      return null;
    } else if (!email.includes("@")) {
      return "Please provide a valid email address";
    } else if (!first_name) {
      return "Please provide a first name";
    } else if (!last_name) {
      return "Please provide a last name";
    } else if (!VALID_NEW_USER_ROLES.includes(role as UserRole)) {
      return "Please provide a valid role";
    } else if (!VALID_TIMEZONES.includes(timezone)) {
      return "Please provide a valid timezone";
    }
  }),
};

export const useOpenDrawerForm = () =>
  useStoreActions((a) => a.drawerForm.open);

export const useCloseDrawerForm = () =>
  useStoreActions((a) => a.drawerForm.close);

export const useDrawerForm = () => {
  const title = useStoreState((a) => a.drawerForm.title);
  const ContentComponent = useStoreState((s) => s.drawerForm.ContentComponent);
  const handleSubmit = useStoreState((s) => s.drawerForm.handleSubmit);

  const isOpen = useStoreState((s) => s.drawerForm.isOpen);

  const open = useOpenDrawerForm();
  const close = useCloseDrawerForm();

  return {
    title,
    ContentComponent,
    handleSubmit,
    isOpen,
    open,
    close,
  };
};

export function useFormErrorMessage(): string | null {
  return useStoreState((s) => s.drawerForm.errorMessage);
}

export function useFieldName2Value(): FieldName2Value {
  return useStoreState((s) => s.drawerForm.fieldName2Value);
}

export function useDataIsSubmittable(): boolean {
  return useStoreState((s) => s.drawerForm.dataIsSubmittable);
}

export function useSetField(): SetFieldActionCreator {
  return useStoreActions((a) => a.drawerForm.setField);
}

export type FieldName = string;
export type FieldValue = string;
export type FieldName2Value = { [name: string]: FieldValue };
interface InitialData {
  fieldName2Value: FieldName2Value;
}
export interface SetFieldPayload {
  name: FieldName;
  value: FieldValue;
}
export type SetFieldActionCreator = ActionCreator<SetFieldPayload>;
